import React, { useState, useCallback } from 'react';
import { PurchaseOrder, OrderStatus } from '@/types';

type Props = {
  orders: PurchaseOrder[];
  onCardDoubleClick: (order: PurchaseOrder) => void;
  onStatusChange: (orderId: string, newStatus: OrderStatus) => Promise<void>;
};

const STATUS_COLUMNS: { key: OrderStatus; label: string }[] = [
  { key: 'OPEN', label: 'No prazo' },
  { key: 'PENDING', label: 'Pendente' },
  { key: 'IN_PROGRESS', label: 'Em execução' },
  { key: 'OVERDUE', label: 'Vencido' },
  { key: 'DELIVERED', label: 'Entregue' },
  { key: 'COMPLETED', label: 'Finalizado' },
  { key: 'CANCELLED', label: 'Cancelado' }
];

const STATUS_COLORS: Record<OrderStatus, { dot: string; headerBorder: string; cardEdge: string; dropBg: string }> = {
  OPEN:        { dot: 'bg-green-400',  headerBorder: 'border-green-300',  cardEdge: 'bg-green-400',  dropBg: 'bg-green-50 dark:bg-green-900/30' },
  PENDING:     { dot: 'bg-yellow-400', headerBorder: 'border-yellow-300', cardEdge: 'bg-yellow-400', dropBg: 'bg-yellow-50 dark:bg-yellow-900/30' },
  APPROVED:    { dot: 'bg-teal-400',   headerBorder: 'border-teal-300',   cardEdge: 'bg-teal-400',   dropBg: 'bg-teal-50 dark:bg-teal-900/30' },
  IN_PROGRESS: { dot: 'bg-indigo-400', headerBorder: 'border-indigo-300', cardEdge: 'bg-indigo-400', dropBg: 'bg-indigo-50' },
  OVERDUE:     { dot: 'bg-red-400',    headerBorder: 'border-red-300',    cardEdge: 'bg-red-400',    dropBg: 'bg-red-50 dark:bg-red-900/30' },
  DELIVERED:   { dot: 'bg-sky-400',    headerBorder: 'border-sky-300',    cardEdge: 'bg-sky-400',    dropBg: 'bg-sky-50' },
  COMPLETED:   { dot: 'bg-blue-500 dark:bg-blue-600',   headerBorder: 'border-blue-300',   cardEdge: 'bg-blue-500 dark:bg-blue-600',   dropBg: 'bg-blue-50 dark:bg-blue-900/30' },
  CANCELLED:   { dot: 'bg-gray-400',   headerBorder: 'border-line-strong dark:border-line-dark-strong',   cardEdge: 'bg-gray-400',   dropBg: 'bg-surface-alt dark:bg-surface-dark-alt' },
};

const OrderKanban: React.FC<Props> = ({ orders, onCardDoubleClick, onStatusChange }) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overColumn, setOverColumn] = useState<OrderStatus | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, orderId: string) => {
    setDraggingId(orderId);
    e.dataTransfer.effectAllowed = 'move';
    // Use both keys for maximum browser compatibility
    e.dataTransfer.setData('text/plain', orderId);
    e.dataTransfer.setData('application/x-order-id', orderId);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setOverColumn(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, colKey: OrderStatus) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setOverColumn(colKey);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Only clear if actually leaving the column (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setOverColumn(null);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>, columnKey: OrderStatus) => {
    e.preventDefault();
    e.stopPropagation();
    setOverColumn(null);

    // Try both data keys
    const orderId =
      e.dataTransfer.getData('application/x-order-id') ||
      e.dataTransfer.getData('text/plain') ||
      draggingId;

    setDraggingId(null);
    if (!orderId) return;

    // Don't call API if dropped in same column
    const order = orders.find(o => o.id === orderId);
    if (order && order.status === columnKey) return;

    await onStatusChange(orderId, columnKey);
  }, [draggingId, orders, onStatusChange]);

  return (
    <div className="flex gap-3 p-3 overflow-x-auto scrollbar-thin">
      {STATUS_COLUMNS.map(col => {
        const colOrders = orders.filter(o => o.status === col.key);
        const colors = STATUS_COLORS[col.key];
        const isOver = overColumn === col.key;
        return (
          <div
            key={col.key}
            onDragOver={(e) => handleDragOver(e, col.key)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.key)}
            className={`rounded-lg border p-2 flex flex-col transition-colors duration-150 flex-1 min-w-[220px] max-w-[320px] ${
              isOver
                ? `${colors.dropBg} border-2 border-dashed border-current`
                : 'bg-bg dark:bg-bg-dark border-line dark:border-line-dark'
            }`}
          >
            {/* Column header */}
            <div className={`mb-3 pb-2 border-b ${colors.headerBorder} flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${colors.dot} inline-block flex-shrink-0`} />
                <h4 className="text-sm font-semibold text-ink-label dark:text-ink-label-dark">{col.label}</h4>
                <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">({colOrders.length})</span>
              </div>
            </div>

            {/* Cards */}
            <div className="space-y-2 overflow-y-auto scrollbar-thin" style={{ maxHeight: '65vh', paddingRight: 4 }}>
              {colOrders.map(order => {
                const isDragging = draggingId === order.id;
                return (
                  <div
                    key={order.id}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, order.id)}
                    onDragEnd={handleDragEnd}
                    onDoubleClick={() => onCardDoubleClick(order)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); onCardDoubleClick(order); }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{
                      opacity: isDragging ? 0.4 : 1,
                      cursor: 'grab',
                      userSelect: 'none',
                    }}
                    className="bg-surface dark:bg-surface-dark rounded-md p-3 shadow-sm hover:shadow-md border border-transparent hover:border-brand-200 dark:border-[rgba(203,106,30,0.30)] focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                    title={order.title}
                  >
                    <div className="flex gap-2">
                      <div className={`w-1 rounded-sm flex-shrink-0 ${colors.cardEdge}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-ink-title dark:text-ink-title-dark truncate">
                          {order.supplier || '— Órgão não informado —'}
                        </div>
                        <div className="mt-1">
                          <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark bg-[#F3F4F6] px-2 py-0.5 rounded">
                            {order.documentType}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {colOrders.length === 0 && (
                <div className="text-xs text-ink-placeholder dark:text-ink-placeholder-dark italic text-center py-4">
                  Nenhum
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderKanban;
